/**
 * Skill data (Depths rework). All auto-firing — the input contract is ONE
 * movement stick, ONE active-ability button (actives.ts, MA), ZERO aiming.
 * There is NO numeric leveling: every skill has a base form and
 * EXACTLY ONE behavioral upgrade (a state flip on the instance, offered as a
 * pick card). Signature skills (signature: true) are locked to a character's
 * slot 0 and are never offered as NEW skills; their upgrade IS offerable.
 *
 * Param columns are archetype-specific (see per-skill comments) — the same
 * convention as the old level tables. base and upgraded params are usually
 * identical: upgrades change BEHAVIOR, not numbers (doc §2).
 *
 * Pool rule (carried over from the Dusk Comet rule): the non-signature pool
 * must always contain at least one ranged emitter, so no signature choice can
 * lock a character out of ranged damage. Lodestar is Hale's signature, so the
 * pool's ranged emitters are Spiral/Ricochet/Swarm plus Hollow Shot (returned
 * to the pool when Vigil was cut, M0) — do not prune the last one.
 */

export type AbilityId =
  | 'lodestar' | 'orbit' | 'undertow' | 'rime'
  | 'ricochet' | 'swarm' | 'witchlight'
  | 'rupture' | 'splinter' | 'caustic' | 'brand' | 'fracture' | 'recoil'
  | 'tithe'
  | 'censer' | 'rotBloom' | 'fanKnives' | 'cleave' | 'prismLance'
  | 'slipleash' | 'boreCharge' | 'hollowShot' | 'leechChain';

/**
 * 'hooked' skills have no tick — they live entirely at World funnels via
 * skillHooks.ts (on-kill queue, damageEnemy, damagePlayer) or in
 * aspects.ts (Slipleash: the hound owns the cadence; Rot Bloom: corpses).
 */
export type Archetype =
  | 'projectile' | 'orbit' | 'undertow' | 'rime'
  | 'ricochet' | 'swarm' | 'witchlight'
  | 'hooked'
  | 'censer' | 'fanKnives' | 'cleave' | 'prismLance'
  | 'boreCharge' | 'hollowShot' | 'leechChain';

/** Offer-table tag: drives per-character weighting + early-offer forgiveness. */
export type SkillTag = 'emitter' | 'onKill' | 'ruleChanger' | 'loop';

/** 6 slots: the signature plus the bar's 5 picks. */
export const ABILITY_SLOTS = 6;

export interface SkillParams {
  cooldown: number;
  damage: number;
  count: number;
  radius: number;
  duration: number;
}

export interface AbilityDef {
  id: AbilityId;
  name: string;
  archetype: Archetype;
  signature: boolean;
  short: string; // HUD glyph letter
  tag: SkillTag;
  base: SkillParams;
  upgraded: SkillParams;
  /** Card title of the one behavioral upgrade. */
  upgradeName: string;
  /** Card body: what the upgrade DOES (behavioral, not numeric). */
  upgradeBlurb: string;
  desc: (p: SkillParams, upgraded: boolean) => string;
}

function params(
  cooldown: number, damage: number, count: number, radius: number, duration: number,
): SkillParams {
  return { cooldown, damage, count, radius, duration };
}

export const ABILITY_DEFS: Record<AbilityId, AbilityDef> = {
  // Hale's signature: the plain aimed volley — deliberately the one skill
  // with nothing strange about it. Params ≈ the old Boltcaster's midtable.
  lodestar: {
    id: 'lodestar',
    name: 'Lodestar',
    archetype: 'projectile',
    signature: true,
    short: 'L',
    tag: 'emitter',
    base: params(0.42, 8, 3, 0, 0),
    upgraded: params(0.42, 8, 3, 0, 0),
    upgradeName: 'Twin Star',
    upgradeBlurb: 'A second volley seeks a second foe.',
    desc: (p, up) =>
      `${p.count} aimed bolts, ${p.damage} dmg, every ${p.cooldown.toFixed(2)}s` +
      (up ? ' — twin volleys' : ''),
  },
  // Orbit field mapping: count = bodies, radius = orbit radius, cooldown =
  // per-body contact tick. Denser crowds = more contacts.
  orbit: {
    id: 'orbit',
    name: 'Orbit',
    archetype: 'orbit',
    signature: false,
    short: 'O',
    tag: 'emitter',
    base: params(0.5, 10, 2, 70, 0),
    upgraded: params(0.5, 10, 2, 70, 0),
    upgradeName: 'Bulwark Ring',
    upgradeBlurb: 'The bodies shove foes back and devour enemy bullets.',
    desc: (p, up) =>
      `${p.count} circling bodies, ${p.damage} contact dmg` +
      (up ? ', shoving foes and eating bullets' : ''),
  },
  // Undertow field mapping: count = inward drag px per pulse, radius = pulse
  // radius, damage = token. Doesn't kill — makes everything else better.
  undertow: {
    id: 'undertow',
    name: 'Undertow',
    archetype: 'undertow',
    signature: false,
    short: 'U',
    tag: 'ruleChanger',
    base: params(1.6, 3, 46, 150, 0),
    upgraded: params(1.6, 3, 46, 150, 0),
    upgradeName: 'Spring Tide',
    upgradeBlurb: 'Pulses also drag loose gems to you.',
    desc: (p, up) =>
      `pulses drag foes ${p.count}px inward (${p.radius}px), ${p.damage} dmg` +
      (up ? ' — gems too' : ''),
  },
  // Rime field mapping: radius = pulse radius, duration = chill seconds.
  // Chilled enemies are slowed (RIME_SLOW) and take RIME_AMP bonus damage
  // (applied at the damageEnemy funnel). The boss and the Keeper cannot be
  // chilled (set-piece pacing); elites can.
  rime: {
    id: 'rime',
    name: 'Rime',
    archetype: 'rime',
    signature: false,
    short: 'R',
    tag: 'ruleChanger',
    base: params(2.2, 4, 0, 160, 2.5),
    upgraded: params(2.2, 4, 0, 160, 2.5),
    upgradeName: 'Deep Freeze',
    upgradeBlurb: 'Chilled regulars below 20% health shatter outright.',
    desc: (p, up) =>
      `chill foes in ${p.radius}px for ${p.duration}s: slowed, take bonus dmg` +
      (up ? '; low ones shatter' : ''),
  },
  // Ricochet field mapping: count = base bounces, radius = bounce seek range.
  // Live bonus bounces at higher body count (computed at fire time).
  ricochet: {
    id: 'ricochet',
    name: 'Ricochet',
    archetype: 'ricochet',
    signature: false,
    short: 'C',
    tag: 'emitter',
    base: params(0.85, 10, 2, 400, 0),
    upgraded: params(0.85, 10, 2, 400, 0),
    upgradeName: 'Windfall',
    upgradeBlurb: "Kills don't spend a bounce.",
    desc: (p, up) =>
      `bounces between targets (${p.count}+ at density), ${p.damage} dmg` +
      (up ? "; kills don't spend a bounce" : ''),
  },
  // Swarm field mapping: cooldown = BASE period (divided by crowd density —
  // fire rate ties to nearby enemy count), count = motes per shot, radius =
  // the density-count radius.
  swarm: {
    id: 'swarm',
    name: 'Swarm',
    archetype: 'swarm',
    signature: false,
    short: 'M',
    tag: 'emitter',
    base: params(0.6, 5, 1, 260, 0),
    upgraded: params(0.6, 5, 1, 260, 0),
    upgradeName: 'Carrion Motes',
    upgradeBlurb: 'A mote that kills survives and seeks again.',
    desc: (p, up) =>
      `homing motes, ${p.damage} dmg; faster the thicker the crowd` +
      (up ? '; killing motes re-seek' : ''),
  },
  // Witchlight field mapping (2026-07-28, Sarah's chain-attack ask): count =
  // jumps after the first link, radius = per-jump seek, damage = per-link.
  // Instant hitscan chain (fireWitchlight — leechChain's shape minus the
  // drink); per-link damage fades ×0.85 per jump unless Undimmed.
  witchlight: {
    id: 'witchlight',
    name: 'Witchlight',
    archetype: 'witchlight',
    signature: false,
    short: 'W',
    tag: 'emitter',
    base: params(1.1, 9, 4, 150, 0),
    upgraded: params(1.1, 9, 4, 150, 0),
    upgradeName: 'Undimmed',
    upgradeBlurb: 'The light loses nothing in the leap.',
    desc: (p, up) =>
      `a stray light leaps through ${p.count + 1} foes, ${p.damage} dmg` +
      (up ? ', undimmed' : ', fading as it goes'),
  },
  // ---- On-kill triggers (hooked; the strongest depth scalers) ----
  // Rupture field mapping: damage = detonation, radius = blast. Chain deaths
  // (viaDetonation) deal ×0.6 unless upgraded.
  rupture: {
    id: 'rupture',
    name: 'Rupture',
    archetype: 'hooked',
    signature: false,
    short: 'X',
    tag: 'onKill',
    base: params(0, 14, 0, 90, 0),
    upgraded: params(0, 14, 0, 90, 0),
    upgradeName: 'Sympathetic Charge',
    upgradeBlurb: 'Chained detonations lose no force.',
    desc: (p, up) =>
      `corpses detonate: ${p.damage} dmg in ${p.radius}px` +
      (up ? ', chains at full force' : ', chains fading'),
  },
  // Splinter field mapping: damage = shard damage. Shatterline: a kill BY a
  // shard fires two shards.
  splinter: {
    id: 'splinter',
    name: 'Splinter',
    archetype: 'hooked',
    signature: false,
    short: 'P',
    tag: 'onKill',
    base: params(0, 8, 1, 0, 0),
    upgraded: params(0, 8, 1, 0, 0),
    upgradeName: 'Shatterline',
    upgradeBlurb: 'A shard kill fires two shards.',
    desc: (p, up) =>
      `each kill fires a shard (${p.damage} dmg) at the nearest foe` +
      (up ? '; shard kills fire two' : ''),
  },
  // Caustic field mapping: damage = pool dps, radius = pool size, duration =
  // pool life. Blight Bloom: deaths inside a pool enlarge and refresh it.
  caustic: {
    id: 'caustic',
    name: 'Caustic',
    archetype: 'hooked',
    signature: false,
    short: 'A',
    tag: 'onKill',
    base: params(0, 12, 0, 70, 3),
    upgraded: params(0, 12, 0, 70, 3),
    upgradeName: 'Blight Bloom',
    upgradeBlurb: 'Deaths inside a pool feed it — bigger, fresher.',
    desc: (p, up) =>
      `kills leave a pool: ${p.damage}/s for ${p.duration}s` +
      (up ? '; deaths inside feed it' : ''),
  },
  // ---- Rule-changers (hooked; multipliers, not adders) ----
  // Brand field mapping: radius = mark radius around the corpse, duration =
  // mark seconds (amp is BRAND_AMP at the funnel), damage = Hexbrand burst.
  brand: {
    id: 'brand',
    name: 'Brand',
    archetype: 'hooked',
    signature: false,
    short: 'B',
    tag: 'ruleChanger',
    base: params(0, 10, 0, 110, 3),
    upgraded: params(0, 10, 0, 110, 3),
    upgradeName: 'Hexbrand',
    upgradeBlurb: 'A branded death burns every branded foe.',
    desc: (p, up) =>
      `kills mark foes in ${p.radius}px: amplified damage for ${p.duration}s` +
      (up ? '; branded deaths burst the marked' : ''),
  },
  // Fracture field mapping: damage = Shatterpoint burst, duration = its stun.
  // Stacking (SHRED_PER_STACK × SHRED_MAX_STACKS) happens at the funnel.
  fracture: {
    id: 'fracture',
    name: 'Fracture',
    archetype: 'hooked',
    signature: false,
    short: 'F',
    tag: 'ruleChanger',
    base: params(0, 12, 0, 0, 0.8),
    upgraded: params(0, 12, 0, 0, 0.8),
    upgradeName: 'Shatterpoint',
    upgradeBlurb: 'At full shred the target shatters — burst, stun, reset.',
    desc: (p, up) =>
      'hits stack armor shred: growing bonus damage' +
      (up ? `; full stacks shatter (${p.damage} dmg, ${p.duration}s stun)` : ''),
  },
  // Recoil field mapping: damage = nova damage, radius = nova radius.
  // Fires from damagePlayer — i-frames are the natural rate limit.
  recoil: {
    id: 'recoil',
    name: 'Recoil',
    archetype: 'hooked',
    signature: false,
    short: 'R',
    tag: 'ruleChanger',
    base: params(0, 16, 0, 150, 0),
    upgraded: params(0, 16, 0, 150, 0),
    upgradeName: 'Concussion',
    upgradeBlurb: 'The nova also swallows enemy bullets.',
    desc: (p, up) =>
      `taking a hit answers with a nova: ${p.damage} dmg in ${p.radius}px` +
      (up ? ', eating bullets' : ''),
  },
  // ---- Loop-facing (hooked; slots only this game can have) ----
  // (Knell and Threshold left this pool in M0.5 — they live on as the
  // Passing Bell / Wayfarer's Ward artifacts. Tithe is the survivor: a live
  // multiplier can't be an artifact hook, and it plays the banking loop.)
  // Tithe field mapping: none — TITHE_RATE/TITHE_CAP in skillHooks govern
  // the live unbanked-gems→damage conversion. Rewards holding, punished by
  // dying; Settlement converts the held bonus into next-floor arrival force.
  tithe: {
    id: 'tithe',
    name: 'Tithe',
    archetype: 'hooked',
    signature: false,
    short: 'G',
    tag: 'loop',
    base: params(0, 0, 0, 0, 0),
    upgraded: params(0, 0, 0, 0, 0),
    upgradeName: 'Settlement',
    upgradeBlurb: 'Descending spends the held bonus as an arrival shock.',
    desc: (_p, up) =>
      'unbanked gems burn as bonus damage until you descend' +
      (up ? '; descent spends them as a shock' : ''),
  },
  // ---- Character signatures (never offered as NEW; upgrades ARE) ----
  // Censer (Wick; AURA since 2026-07-27 — orbit placement was too awkward):
  // radius = aura radius, damage = aura dps, cooldown = trail-drop period
  // (upgrade only), duration = trail burn-zone life. count unused.
  censer: {
    id: 'censer',
    name: 'Censer',
    archetype: 'censer',
    signature: true,
    short: 'E',
    tag: 'emitter',
    base: params(0.5, 14, 1, 80, 1.4),
    upgraded: params(0.5, 14, 1, 80, 1.4),
    upgradeName: 'Trail of Embers',
    upgradeBlurb: 'His footsteps leave burning ground.',
    desc: (p, up) =>
      `smoldering aura: ${p.damage}/s to everything within reach` +
      (up ? '; walking paints burning ground' : ''),
  },
  // Rot Bloom (Mourne): corpses feed the reserve; crawlers spawn from it
  // (aspects.ts owns everything). count = crawlers per corpse-spend, damage =
  // crawler contact dmg (rides damageMult at the bite — M0 retune: 7 raw →
  // 5 scaled, ~60/40 swarm-vs-skills), duration = crawler life.
  rotBloom: {
    id: 'rotBloom',
    name: 'Rot Bloom',
    archetype: 'hooked',
    signature: true,
    short: 'N',
    tag: 'onKill',
    // 2026-07-27 retune (probe: floor-3 base collapsed to ~10 k/min — the
    // chain breaks against depth-scaled HP): bites 5→6, TTL 7→9 for a
    // bigger standing swarm. Floor 1 is spawn-capped, so no runaway there.
    base: params(0.8, 6, 1, 0, 9),
    upgraded: params(0.8, 6, 2, 0, 9),
    upgradeName: 'Ossuary Chorus',
    upgradeBlurb: 'Each corpse sprouts a second crawler.',
    desc: (p, up) =>
      `corpses sprout crawlers (${p.damage} dmg bites, ${p.duration}s)` +
      (up ? ', in pairs' : '') +
      '; the rot births its own when the swarm runs thin',
  },
  // Fan of Knives (Vane): count = base knives — LIVE bonus knives with move
  // speed (the aspect axis made a weapon), radius = cone half-angle ×100.
  fanKnives: {
    id: 'fanKnives',
    name: 'Fan of Knives',
    archetype: 'fanKnives',
    signature: true,
    short: 'V',
    tag: 'emitter',
    base: params(0.9, 7, 5, 50, 0),
    upgraded: params(0.9, 7, 5, 50, 0),
    upgradeName: 'Ninefold Fan',
    upgradeBlurb: 'The fan opens behind you as well.',
    desc: (p, up) =>
      `cone of ${p.count}+ knives — more the faster you move` +
      (up ? ', front and back' : ''),
  },
  // Cleave (Barrow): pulse whose damage grows with bodies in range
  // (+15%/extra enemy). radius = reach.
  cleave: {
    id: 'cleave',
    name: 'Cleave',
    archetype: 'cleave',
    signature: true,
    short: 'B',
    tag: 'emitter',
    base: params(0.7, 9, 0, 120, 0),
    upgraded: params(0.7, 9, 0, 120, 0),
    upgradeName: 'Whirlbreaker',
    upgradeBlurb: 'Connecting cleaves shove the crowd back.',
    desc: (p, up) =>
      `spinning cleave: ${p.damage} dmg, stronger per foe in reach` +
      (up ? ', knocking them back' : ''),
  },
  // Prism Lance (Trine): p1 = split generations; each hit splits the beam.
  prismLance: {
    id: 'prismLance',
    name: 'Prism Lance',
    archetype: 'prismLance',
    signature: true,
    short: 'Z',
    tag: 'emitter',
    base: params(1.0, 14, 2, 0, 0),
    upgraded: params(1.0, 14, 3, 0, 0),
    upgradeName: 'Trine Refraction',
    upgradeBlurb: 'One more generation of splits.',
    desc: (p, up) =>
      `lance that splits on each hit (${p.count} generations)` +
      (up ? ' — a cascade' : ''),
  },
  // Slipleash (Bram & Gaunt): the hound charges on its own cadence — the
  // whole skill lives on World.hound (aspects.ts). damage = dash damage,
  // cooldown = charge period. M0 buff: 2.2s/16 → 1.8s/20. Retune
  // 2026-07-27: Gaunt also BITES whatever stands beside him between
  // charges (HOUND_BITE_* in aspects.ts) — the probe showed dash-only
  // couldn't out-kill spawn rate at any depth (4-16 k/min vs ~74).
  slipleash: {
    id: 'slipleash',
    name: 'Slipleash',
    archetype: 'hooked',
    signature: true,
    short: 'D',
    tag: 'emitter',
    base: params(1.8, 20, 0, 0, 0),
    upgraded: params(1.3, 20, 0, 0, 0),
    upgradeName: 'Loosed',
    upgradeBlurb: 'Gaunt charges and bites more eagerly.',
    desc: (p, up) =>
      `the hound charges the nearest foe (${p.damage} dmg) every ${p.cooldown.toFixed(1)}s, ` +
      'worrying whatever stands beside him' +
      (up ? ' — eagerly' : ''),
  },
  // Bore-charge (Cask): slow lobbed blast with REAL friendly fire on Cask
  // himself (selfDamage path — tier never scales your own bombs).
  boreCharge: {
    id: 'boreCharge',
    name: 'Bore-charge',
    archetype: 'boreCharge',
    signature: true,
    short: 'Q',
    tag: 'emitter',
    // Upgrade (2026-07-27, was Double Load): ONE heavier bomb instead of a
    // pair — same-ish throughput without doubling his self-burn exposure
    // (the probe showed him scorching to 1 max HP in crowds).
    // Radius deliberately UNCHANGED on upgrade: a wider blast reached back
    // to Cask more often (probe: dmg/min 17→25) — denser, not wider.
    base: params(1.5, 22, 1, 110, 0),
    upgraded: params(1.5, 40, 1, 110, 0),
    upgradeName: 'Overcharge',
    upgradeBlurb: 'One charge, packed triple.',
    desc: (p, up) =>
      `lobbed charge: ${p.damage} dmg in ${p.radius}px — you included` +
      (up ? ', packed heavy' : ''),
  },
  // Hollow Shot: slow, heavy, piercing (p1 = pierces). Was Vigil's signature;
  // returned to the shared pool when he was cut (keeps ranged access healthy).
  hollowShot: {
    id: 'hollowShot',
    name: 'Hollow Shot',
    archetype: 'hollowShot',
    signature: false,
    short: 'H',
    tag: 'emitter',
    base: params(1.4, 30, 3, 0, 0),
    upgraded: params(1.4, 30, 5, 0, 0),
    upgradeName: 'Long Hollow',
    upgradeBlurb: 'The shot refuses to stop.',
    desc: (p, up) =>
      `slow heavy shot: ${p.damage} dmg, pierces ${p.count}` +
      (up ? '+ foes' : ' foes'),
  },
  // Leech Chain (Thirl): count = jumps, radius = per-jump seek; yanks foes
  // toward him and drinks (his ONLY recovery under Ebb).
  leechChain: {
    id: 'leechChain',
    name: 'Leech Chain',
    archetype: 'leechChain',
    signature: true,
    short: 'L',
    tag: 'emitter',
    base: params(0.8, 10, 3, 170, 0),
    upgraded: params(0.8, 10, 5, 190, 0),
    upgradeName: 'Red Harvest',
    upgradeBlurb: 'The chain reaches further and drinks deeper.',
    desc: (p, up) =>
      `whip-chain: yank and drain ${p.count + 1} foes (${p.damage} dmg)` +
      (up ? ', deeply' : ''),
  },
};

export const ABILITY_IDS = Object.keys(ABILITY_DEFS) as AbilityId[];

export interface AbilityInstance {
  defId: AbilityId;
  /** The one behavioral upgrade taken (state flip, no levels). */
  upgraded: boolean;
  cooldown: number;
  /** Orbit phase. */
  angle: number;
  /** Orbit per-body contact tick / Censer trail-drop tick. */
  contactTick: number;
}

/** Current params for an instance: upgraded flips the table. */
export function skillParams(inst: AbilityInstance): SkillParams {
  const def = ABILITY_DEFS[inst.defId];
  return inst.upgraded ? def.upgraded : def.base;
}

export function makeAbility(defId: AbilityId): AbilityInstance {
  return {
    defId, upgraded: false, cooldown: 0.3, angle: 0, contactTick: 0,
  };
}
